import * as React from 'react'
import { createPortal } from 'react-dom'

import { ContextMenuPanel } from './components/context-menu-panel'
import { useContextMenuActiveIndex } from './hooks/use-context-menu-active-index'
import { useContextMenuPosition } from './hooks/use-context-menu-position'

export interface EditorContextMenuShellProps {
  open: boolean
  position: { x: number; y: number }
  onOpenChange: (open: boolean) => void
  /** Visible canvas scroller; menu is clamped inside its client rect (above scrollbars). */
  viewportBoundsRef?: React.RefObject<HTMLElement | null>
  children: React.ReactNode
}

export function EditorContextMenuShell({
  open,
  position,
  onOpenChange,
  viewportBoundsRef,
  children,
}: EditorContextMenuShellProps) {
  const menuRef = React.useRef<HTMLDivElement>(null)

  // Position runs before the active index: its three effects precede the index reset, highlight
  // and keydown effects, as they did when all six lived in this component.
  const { resolvedPosition, maxHeight } = useContextMenuPosition({
    open,
    position,
    viewportBoundsRef,
    menuRef,
  })

  useContextMenuActiveIndex({ open, position, onOpenChange, menuRef, children })

  if (!open) return null

  // Portalled to `document.body` and layered above the floating toolbars (z-index 9999–10000).
  // Those are portalled there too, so a menu rendered inside the canvas tree opened underneath a
  // toolbar that was already up, such as the Text on a Path card (WMPCM-12967).
  return createPortal(
    <div
      className="fixed inset-0 z-[10001]"
      data-editor-context-menu="true"
      onClick={() => onOpenChange(false)}
      onContextMenu={(e) => {
        e.preventDefault()
        onOpenChange(false)
      }}
    >
      <ContextMenuPanel
        ref={menuRef}
        left={resolvedPosition.left}
        top={resolvedPosition.top}
        maxHeight={maxHeight}
      >
        {children}
      </ContextMenuPanel>
    </div>,
    document.body
  )
}
